/**
 * What the hand-off card is showing, decided from what the host wrote.
 *
 * Pure, so each state is testable without rendering the card. The host writes
 * one object to `/results/<componentId>`: a receipt from the console's stage
 * capability, `{ status: 'rejected', reason, message }` when it refused the
 * whole proposal, or `{ error: true, message }` when it failed.
 */

/** A legal value offered in place of a refused one. */
export interface HandoffAlternative {
  readonly label: string;
  /** The refused command with this value instead, ready to dispatch. */
  readonly command: Record<string, unknown>;
}

/** A change the quote would not take, named the way the grid names it. */
export interface HandoffRefusedChange {
  readonly id: string;
  readonly label: string;
  readonly fieldLabel?: string;
  readonly toLabel: string;
  readonly message: string;
  readonly alternatives?: readonly HandoffAlternative[];
}

/** Where a follow-up change-set from this card is addressed. */
export interface HandoffFollowUp {
  readonly viewKey: string;
  readonly viewId: string;
  readonly entityId: string;
  readonly baseRevision: number;
  /** Every change-set this card already describes. */
  readonly changeSetIds: readonly string[];
}

export type HandoffState =
  | 'checking'
  | 'ready'
  | 'partial'
  | 'not-added'
  | 'blocked'
  | 'unconfirmed'
  | 'historical'
  | 'unbound';

/** The fields of the host's result this decision reads. */
export interface HandoffStateResult {
  readonly status?: string;
  readonly rows?: readonly unknown[];
  readonly refused?: readonly unknown[];
  readonly historical?: boolean;
  readonly error?: boolean;
  readonly message?: string;
}

export interface HandoffStateInputs {
  readonly result?: HandoffStateResult;
  /** Rows passed as a prop, for a card rendered without a host result. */
  readonly rows?: readonly unknown[];
  readonly historical?: boolean;
  /** Whether a staging action is bound, so a result is on its way. */
  readonly bound: boolean;
}

export function handoffState({
  result,
  rows: rowsProp,
  historical,
  bound,
}: HandoffStateInputs): HandoffState {
  if (result?.historical === true || historical === true) {
    return 'historical';
  }
  if (result?.status === 'rejected' || result?.error === true) {
    return 'blocked';
  }

  const rows = result?.rows ?? rowsProp ?? [];
  const refused = result?.refused ?? [];
  if (rows.length > 0) {
    return refused.length > 0 ? 'partial' : 'ready';
  }
  // Something was dispatched but never showed up on the draft in time.
  if (result?.status === 'staged-pending') {
    return 'unconfirmed';
  }
  if (refused.length > 0 || result?.status === 'not-added') {
    return 'not-added';
  }
  if (result?.status !== undefined) {
    // A receipt with nothing added and nothing refused: the wait ended before
    // the change reached the draft.
    return 'unconfirmed';
  }
  return bound ? 'checking' : 'unbound';
}

/** The fields of the host's result a follow-up is built from. */
export interface FollowUpSource {
  readonly rows?: readonly unknown[];
  readonly refused?: readonly HandoffRefusedChange[];
  readonly followUp?: HandoffFollowUp;
  readonly followUpError?: string;
}

/**
 * The change-set a "Use $25,000" button sends. Its receipt replaces this card's
 * result, so it carries what the card shows.
 */
export function followUpChangeSet(
  source: FollowUpSource,
  refusedId: string,
  alternative: HandoffAlternative,
  changeSetId: string
): Record<string, unknown> | null {
  const base = source.followUp;
  if (!base) {
    return null;
  }
  // The last follow-up's outcome is not part of the card being carried.
  const card = Object.fromEntries(
    Object.entries(source).filter(
      ([key]) => key !== 'followUpError' && key !== 'failedChangeSetId'
    )
  );
  return {
    viewKey: base.viewKey,
    viewId: base.viewId,
    entityId: base.entityId,
    baseRevision: base.baseRevision,
    changeSetId,
    commands: [alternative.command],
    carry: {
      changeSetIds: base.changeSetIds,
      rows: source.rows ?? [],
      refused: source.refused ?? [],
      resolving: refusedId,
      // Stored back by the host if the follow-up fails before a receipt.
      card,
    },
  };
}

/** A "Use $25,000" in flight: which refused change, and its change-set. */
export interface PendingFollowUp {
  readonly id: string;
  readonly label: string;
  readonly changeSetId: string;
}

/** What the card shows, and the follow-up it is waiting on. */
export interface CardView<T> {
  readonly shown?: T;
  readonly pending: PendingFollowUp | null;
}

type ArrivingResult = HandoffStateResult & {
  readonly followUp?: Partial<HandoffFollowUp>;
  readonly followUpError?: string;
  /** Set by the host when it stores the card back after a follow-up failed. */
  readonly failedChangeSetId?: string;
};

const isFailure = (result: ArrivingResult) =>
  result.status === 'rejected' || result.error === true;

/** A receipt from the host, as opposed to a failure or nothing yet. */
const isReceipt = (result: ArrivingResult | undefined): boolean =>
  result !== undefined && !isFailure(result) && result.followUp !== undefined;

/**
 * Fold a new host result into what the card shows. Once the card has a
 * receipt, a failure or a missing result never replaces it: those can only come
 * from a follow-up, and the card's rows are still on the quote. A follow-up
 * settles only on a receipt naming its change-set.
 */
export function resultArrived<T extends ArrivingResult>(
  view: CardView<T>,
  incoming: T | undefined
): CardView<T> {
  const { pending, shown } = view;
  if (isReceipt(shown) && shown) {
    if (!incoming) {
      return view;
    }
    if (isFailure(incoming)) {
      return {
        shown: {
          ...shown,
          followUpError:
            incoming.message ??
            shown.followUpError ??
            'That change couldn’t be added.',
        },
        pending: null,
      };
    }
  }
  if (!pending || !incoming) {
    return { shown: incoming, pending: null };
  }
  // A follow-up's own receipt, or the card stored back with why it failed.
  if (
    incoming.followUp?.changeSetIds?.includes(pending.changeSetId) ||
    incoming.failedChangeSetId === pending.changeSetId
  ) {
    return { shown: incoming, pending: null };
  }
  return { shown: incoming, pending };
}
